import { useState, useRef, PropsWithChildren, useEffect } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";
import { ColumnLayout } from "@/libraries/design/foundations";

const containerStyles = css({
    "transformOrigin": "50% 0%",
    "transition": `transform 750ms ${wacToken("motion.easing.ease-both")}`,
    "@media (prefers-reduced-motion: no-preference)": {
        [media.above.sm]: {
            "&.initial": {
                transform: "scale(0.7925)"
            }
        }
    }
});

export const ScrollToEnlarge = ({ children }: PropsWithChildren) => {
    const [isTransitioned, setIsTransitioned] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Track scroll position
    useEffect(() => {
        // If the browser does not support Scroll Timeline, we just enlarge the content
        // If the browser does support Scroll Timeline, we trigger the animation after the user has scrolled 80px down
        if (
            typeof window !== "undefined" &&
            // @ts-ignore
            typeof window?.ScrollTimeline !== "undefined" &&
            containerRef.current
        ) {
            // @ts-ignore
            const timeline = new ScrollTimeline({
                source: document.documentElement,
                axis: "block"
            });

            /* 
                This faux animation is used instead of a scroll event listener for simplicity and performance benefit.
                The faux animation does not actually animate anything, but is used to capture when we have scrolled enough 
                to trigger the real animation upon the faux animation's completion.
            */
            const enter = containerRef.current.animate(
                {
                    opacity: [1, 1]
                },
                {
                    timeline,
                    // @ts-ignore
                    rangeStart: "exit-crossing 79px",
                    // @ts-ignore
                    rangeEnd: "exit-crossing 80px"
                }
            );

            if (enter) {
                enter.onfinish = () => {
                    setIsTransitioned(true);
                };
            }
        } else {
            setIsTransitioned(true);
        }
    }, [containerRef]);

    return (
        <ColumnLayout layout="10">
            <div
                ref={containerRef}
                css={containerStyles}
                className={isTransitioned ? "loaded" : "initial"}
            >
                {children}
            </div>
        </ColumnLayout>
    );
};
